/**
 * Custom hook for managing customer form state with debounced auto-save
 */

import React from 'react';
import { customerService } from '../../../../services/customer.service';
import { useAuth } from '../../../../hooks/useAuth';
import type { CustomerData, Customer } from '../types';

export const useCustomerForm = (customerId: string | undefined, customer: Customer | undefined) => {
  const { user } = useAuth();
  const isCustomerRole = user?.role === 'customer';

  const [customerData, setCustomerData] = React.useState<CustomerData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
    admin_notes: '',
  });

  const debounceTimers = React.useRef<{ [key: string]: ReturnType<typeof setTimeout> }>({});

  // Initialize customer data when customer changes
  React.useEffect(() => {
    if (customer) {
      setCustomerData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        notes: customer.notes || '',
        admin_notes: customer.adminNotes || '',
      });
    }
  }, [customer]);

  // Cleanup debounce timers on unmount
  React.useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  /**
   * Handle customer field change with debounced auto-save
   */
  const handleCustomerFieldChange = (field: keyof CustomerData, value: string) => {
    if (!customerId) return;

    // Customers cannot edit admin_notes
    if (isCustomerRole && field === 'admin_notes') {
      return;
    }

    // Update local state immediately
    setCustomerData({ ...customerData, [field]: value });

    // Clear existing timer for this field
    if (debounceTimers.current[field]) {
      clearTimeout(debounceTimers.current[field]);
    }

    // Set new debounced save timer (500ms delay)
    debounceTimers.current[field] = setTimeout(async () => {
      try {
        // Use /api/v1/customers/me endpoint for customer role
        if (isCustomerRole) {
          await customerService.updateOwnProfile({ [field]: value });
        } else {
          await customerService.updateCustomer(customerId, { [field]: value });
        }
      } catch (error) {
        console.error(`Failed to update customer ${field}:`, error);
        // Revert on error
        if (customer) {
          setCustomerData({
            name: customer.name || '',
            email: customer.email || '',
            phone: customer.phone || '',
            address: customer.address || '',
            notes: customer.notes || '',
            admin_notes: customer.adminNotes || '',
          });
        }
        alert(`Failed to update ${field}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }, 500);
  };

  return {
    customerData,
    handleCustomerFieldChange,
  };
};
